/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: Claw 团队访问权限配置
 *
 * 数据来源（优先级从高到低）：
 *   1. 远程 KV 配置 todayclaw_entry_config（通过 /web/config/query 拉取，存入 Redux）
 *   2. 本地硬编码 FALLBACK_CONFIG（远程配置未拉取到时兜底）
 *
 * 使用说明：
 *   - 后台 keyValue 配置 todayclaw_entry_config 的 value 为 JSON 字符串
 *   - JSON 结构：{ enabled: boolean, teams: { [teamId]: ClawTeamAccess } }
 *   - 修改后台配置即时生效（用户刷新页面后），无需重新部署
 */

// ============ 类型定义 ============

/** 套餐类型：1=小虾入门版 2=中虾标准版 3=大虾旗舰版 */
type ClawPlanType = 1 | 2 | 3

/**
 * 可见范围（权限控制）— 对齐项目 memberRoleTypes
 *
 * 项目角色层级（从高到低）：
 *   MASTER_ADMIN(1) 主管理员 → 团队创建者，最高权限
 *   ADMIN(2)        管理员   → 被主管理员授权的管理员
 *   DEPARTMENT_ADMIN(3) 部门管理员 → 仅管理所属部门
 *   NORMAL(0)       普通成员
 *
 * visibility 配置项含义：
 * - master_admin:  仅主管理员可见
 * - admin:         主管理员 + 管理员可见
 * - dept_admin:    主管理员 + 管理员 + 部门管理员可见
 * - all:           团队所有成员可见
 */
type ClawVisibility = 'master_admin' | 'admin' | 'dept_admin' | 'all'

interface ClawTeamAccess {
  /** 团队 ID（invite_id 或 groupId） */
  teamId: string
  /** 套餐类型：1=小虾 2=中虾 3=大虾 */
  plan: ClawPlanType
  /** 订单号 */
  orderId: string
  /** 联系人 */
  contact: string
  /** 开通日期 YYYY-MM-DD */
  activatedAt: string
  /** 有效期截止日期 YYYY-MM-DD */
  expiredAt: string
  /** 可见范围，默认 admin */
  visibility?: ClawVisibility
  /** 备注（可选） */
  remark?: string
}

/**
 * 套餐售卖开关
 * key 为套餐类型（1/2/3），value 为是否开放购买
 * 未配置的套餐默认开放
 */
type ClawPlanSwitch = Partial<Record<ClawPlanType, boolean>>

interface ClawAccessEnvConfig {
  /** 总开关：false 则所有团队都不显示入口 */
  enabled: boolean
  /** 已付款团队列表 */
  teams: ClawTeamAccess[]
  /** 套餐售卖开关：控制每个套餐是否可购买 */
  planSwitch?: ClawPlanSwitch
  /**
   * 黑名单：对已签约大B端客户隐藏龙虾Claw入口
   * groupId 同时匹配 groupId 和 invite_id（短ID）
   * 配置示例：[{ "groupId": "123456", "groupName": "明阳集团" }]
   */
  blacklist?: { groupId: string; groupName: string }[]
}

// ============ 套餐类型映射 ============

export const PLAN_TYPE_MAP: Record<
  ClawPlanType,
  { key: string; name: string }
> = {
  1: { key: 'starter', name: '小虾 Claw 入门版' },
  2: { key: 'standard', name: '中虾 Claw 标准版' },
  3: { key: 'flagship', name: '大虾 Claw 旗舰版' }
}

// ============ 本地兜底配置（远程配置未就绪时使用） ============

const FALLBACK_CONFIG: ClawAccessEnvConfig = {
  enabled: true,
  teams: []
}

// ============ 远程配置解析 ============

/** KV 配置 key 名称，与 config.js KVParams.names 中一致 */
export const CLAW_CONFIG_KEY = 'todayclaw_entry_config'

/**
 * 解析远程 KV 配置的 JSON 字符串
 *
 * 远程配置结构（含环境区分）：
 * {
 *   "test":       { "enabled": true, "teams": { ... } },
 *   "production": { "enabled": true, "teams": { ... } }
 * }
 *
 * 根据 process.env.NODE_ENV 自动选择对应环境
 *
 * @param rawValue - Redux state.main.config.todayclaw_entry_config
 * @returns 当前环境对应的配置，解析失败返回 null
 */
export function parseRemoteConfig(
  rawValue: string | undefined | null
): ClawAccessEnvConfig | null {
  if (!rawValue) return null
  try {
    const parsed = JSON.parse(rawValue)

    // 按当前环境取对应配置
    const envKey = process.env.NODE_ENV === 'production' ? 'production' : 'test'
    const envConfig = parsed[envKey] || parsed

    // 校验：必须有 enabled 和 teams 字段
    if (
      typeof envConfig.enabled !== 'boolean' ||
      !Array.isArray(envConfig.teams)
    ) {
      console.warn('[ClawConfig] 远程配置格式不正确:', envKey, envConfig)
      return null
    }
    return envConfig as ClawAccessEnvConfig
  } catch (e) {
    console.warn('[ClawConfig] 远程配置 JSON 解析失败:', e)
    return null
  }
}

// ============ 核心判断函数 ============

/**
 * 判断当前用户是否可以看到 Claw 工作台入口
 *
 * @param teamId    - invite_id 或 groupId
 * @param myRole    - Redux myRole 数值（MASTER_ADMIN=1, ADMIN=2, DEPARTMENT_ADMIN=3, NORMAL=0）
 * @param remoteConfigValue - Redux state.main.config.todayclaw_entry_config（可选）
 */
export function isClawActivated(
  teamId: string,
  myRole: number = 0,
  remoteConfigValue?: string | null
): boolean {
  if (!teamId) return false
  // 优先使用远程配置，解析失败则用本地兜底
  const config = parseRemoteConfig(remoteConfigValue) || FALLBACK_CONFIG
  if (!config.enabled) return false
  const team = config.teams.find((t) => t.teamId === teamId)
  if (!team) return false
  // 检查有效期
  const now = new Date().toISOString().slice(0, 10) // YYYY-MM-DD
  if (now > team.expiredAt) return false
  // 检查可见范围（默认 admin）
  const visibility = team.visibility || 'admin'
  // MASTER_ADMIN=1 始终可见（只要团队已配置且未过期）
  if (myRole === 1) return true
  if (visibility === 'master_admin') return false
  // ADMIN=2
  if (myRole === 2) return true
  if (visibility === 'admin') return false
  // DEPARTMENT_ADMIN=3
  if (myRole === 3) return true
  if (visibility === 'dept_admin') return false
  // visibility === 'all' → 所有成员可见
  return visibility === 'all'
}

/**
 * 获取团队的 Claw 访问信息
 * @param teamId - invite_id 或 groupId
 * @param remoteConfigValue - 远程配置原始值
 * @returns 团队配置信息，未配置或已过期则返回 null
 */
export function getClawTeamAccess(
  teamId: string,
  remoteConfigValue?: string | null
): ClawTeamAccess | null {
  if (!teamId) return null
  const config = parseRemoteConfig(remoteConfigValue) || FALLBACK_CONFIG
  if (!config.enabled) return null
  const team = config.teams.find((t) => t.teamId === teamId) || null
  if (!team) return null
  const now = new Date().toISOString().slice(0, 10)
  return now <= team.expiredAt ? team : null
}

/**
 * 获取 Claw 入口总开关状态
 */
export function isClawEnabled(remoteConfigValue?: string | null): boolean {
  const config = parseRemoteConfig(remoteConfigValue) || FALLBACK_CONFIG
  return config.enabled
}

/**
 * 判断指定套餐是否可购买
 *
 * @param planType - 套餐类型 1/2/3
 * @param remoteConfigValue - 远程配置原始值
 * @returns true=可购买，false=已关闭（资格已抢光）
 */
export function isPlanAvailable(
  planType: ClawPlanType,
  remoteConfigValue?: string | null
): boolean {
  const config = parseRemoteConfig(remoteConfigValue) || FALLBACK_CONFIG
  // 未配置 planSwitch 或该套餐未配置时，默认开放
  if (!config.planSwitch) return true
  const val = config.planSwitch[planType]
  return val !== false
}

/**
 * 获取所有套餐的开关状态
 */
export function getPlanSwitchMap(
  remoteConfigValue?: string | null
): Record<ClawPlanType, boolean> {
  const config = parseRemoteConfig(remoteConfigValue) || FALLBACK_CONFIG
  const sw = config.planSwitch || {}
  return {
    1: sw[1] !== false,
    2: sw[2] !== false,
    3: sw[3] !== false
  }
}

/**
 * 判断当前团队是否在黑名单中（大B端客户屏蔽 Claw 入口）
 *
 * 匹配策略：用 groupId 匹配黑名单中的 groupId
 *
 * @param groupId           - 团队 groupId
 * @param teamShortId       - 团队短 ID（invite_id），备用匹配
 * @param remoteConfigValue - 远程配置原始值
 */
export function isClawBlacklisted(
  groupId: string,
  teamShortId: string,
  remoteConfigValue?: string | null
): boolean {
  const config = parseRemoteConfig(remoteConfigValue) || FALLBACK_CONFIG
  const blacklist = config.blacklist
  if (!Array.isArray(blacklist) || blacklist.length === 0) return false
  const gid = String(groupId || '').trim()
  const sid = String(teamShortId || '').trim()
  return blacklist.some((item) => {
    const bid = String(item?.groupId || '').trim()
    if (!bid) return false
    return bid === gid || bid === sid
  })
}

export type {
  ClawTeamAccess,
  ClawPlanType,
  ClawPlanSwitch,
  ClawVisibility,
  ClawAccessEnvConfig
}
export { FALLBACK_CONFIG }
